import { allowMultiLanguage } from '@crema/constants/AppConst';
import { useSidebarContext } from '@crema/context/AppContextProvider/SidebarContextProvider';
import { useThemeContext } from '@crema/context/AppContextProvider/ThemeContextProvider';
import IntlMessages from '@crema/helpers/IntlMessages';
import { checkPermission } from '@crema/helpers/RouteHelper';
import { useAuthUser } from '@lbs/hook/AuthHooks';
import { ChevronLeft, ChevronRight, ExpandMore } from '@mui/icons-material';
import { Collapse, Icon, IconButton, ListItemText } from '@mui/material';

import Box from '@mui/material/Box';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import VerticalItem from '../VerticalItem';
import VerticalCollapseItem from './VerticalCollapseItem';

const needsToBeOpened = (pathname, item) => {
    return pathname && isUrlInChildren(item, pathname);
};

const isUrlInChildren = (parent, url) => {
    if (!parent.children) {
        return false;
    }

    for (const element of parent.children) {
        if (element.children) {
            if (isUrlInChildren(element, url)) {
                return true;
            }
        }

        if (element.url === url || url.includes(element.url)) {
            return true;
        }
    }

    return false;
};

const VerticalCollapse = ({ item, level }) => {
    const { theme } = useThemeContext();
    const { sidebarTextColor } = useSidebarContext();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(() => needsToBeOpened(pathname, item));

    useEffect(() => {
        if (needsToBeOpened(pathname, item)) {
            setOpen(true);
        }
    }, [pathname, item]);

    const handleClick = () => {
        setOpen(!open);
    };

    const { user } = useAuthUser();
    const hasPermission = useMemo(
        () => checkPermission(item.permittedRole, user.role),
        [item.permittedRole, user.role],
    );

    const arrowIcon = useMemo(() => {
        if (open) return <ExpandMore />;
        return theme.direction === 'ltr' ? <ChevronRight /> : <ChevronLeft />;
    }, [open, theme.direction]);

    if (!hasPermission) {
        return null;
    }

    return (
        <>
            <VerticalCollapseItem
                level={level}
                sidebarTextColor={sidebarTextColor}
                button
                component='div'
                className={clsx('menu-vertical-collapse', open && 'open')}
                onClick={handleClick}
            >
                {item.icon && (
                    <Box component='span'>
                        <Icon
                            sx={{ mr: 4 }}
                            color='action'
                            className={clsx('nav-item-icon')}
                        >
                            {item.icon}
                        </Icon>
                    </Box>
                )}
                <ListItemText
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 14,
                    }}
                    className='nav-item-content'
                    classes={{ primary: clsx('nav-item-text') }}
                    primary={
                        allowMultiLanguage ? (
                            <IntlMessages id={item.messageId} />
                        ) : (
                            item.title
                        )
                    }
                />
                <IconButton
                    className='nav-item-icon-arrow-btn'
                    sx={{ p: 0, mr: 0.75 }}
                    disableRipple
                    size='large'
                >
                    {arrowIcon}
                </IconButton>
            </VerticalCollapseItem>

            {item.children && (
                <Collapse in={open} className='collapse-children'>
                    {item.children.map((item) => (
                        <React.Fragment key={item.id}>
                            {item.type === 'collapse' && (
                                <VerticalCollapse
                                    item={item}
                                    level={level + 1}
                                />
                            )}

                            {item.type === 'item' && (
                                <VerticalItem item={item} level={level + 1} />
                            )}
                        </React.Fragment>
                    ))}
                </Collapse>
            )}
        </>
    );
};

VerticalCollapse.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
        permittedRole: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
        children: PropTypes.array,
        messageId: PropTypes.string,
        type: PropTypes.string,
    }),
    level: PropTypes.number,
};
VerticalCollapse.defaultProps = {};

export default React.memo(VerticalCollapse);
